/*
 * Roam System Recommender — guided "help me choose" path.
 *
 * A short questionnaire about the boat, then a recommendation engine maps each
 * answer to specific Roam products (with a plain-English reason), picks the
 * right plan tier, and adds the whole system to the cart in ONE /cart/add.js
 * call with a shared _kit_id — same mechanism as roam-configurator.js.
 *
 * Wired-input budget = 3 (hub IN2-IN4; IN1 = main battery voltage, onboard).
 * Priority for the 3 cable inputs: bilge pumps -> float switches -> batteries;
 * overflow falls back to wireless equivalents.
 */
(function () {
  'use strict';

  var WIRED_BUDGET = 3;
  var TIER_RANK = { base: 1, advanced: 2, premium: 3 };
  var TIERS = {
    base: { name: 'Base', price: '$5/mo billed annually', blurb: 'cellular monitoring anywhere + your wired sensors' },
    advanced: { name: 'Advanced', price: '$7.50/mo billed annually', blurb: 'adds Roam wireless, Ruuvi and Victron sensors' },
    premium: { name: 'Premium', price: '$10/mo billed annually', blurb: 'adds NMEA 2000® data — depth, wind, GPS, tanks, engine, etc.' }
  };

  function formatMoney(cents) {
    if (window.Shopify && Shopify.formatMoney && window.theme && theme.moneyFormat) {
      try { return Shopify.formatMoney(cents, theme.moneyFormat); } catch (e) {}
    }
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }
  function kitId() { return 'rec-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7); }
  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  var STEPS = [
    { key: 'banks', title: 'How many battery banks do you have?',
      help: 'Most boats have a house bank, sometimes a separate starter and a generator bank.',
      type: 'steppers', fields: [
        { id: 'house', label: 'House' }, { id: 'starter', label: 'Starter' }, { id: 'gen', label: 'Generator' }
      ] },
    { key: 'chem', title: 'What kind of batteries are they?',
      help: 'We monitor lithium differently — its voltage stays flat, so voltage alone won’t tell you the real charge.',
      type: 'choice', field: 'chem',
      options: [{ v: 'lead', l: 'Lead-acid' }, { v: 'lithium', l: 'Lithium' }, { v: 'mixed', l: 'A mix of both' }] },
    { key: 'bankchem', title: 'Which of your banks are lithium?',
      help: 'Mark each one — we recommend a shunt where it matters (lithium) and simple voltage monitoring where it’s fine (lead-acid).',
      type: 'bankchem' },
    { key: 'fridge', title: 'How many fridges or freezers aboard?',
      help: 'We put a wireless temperature sensor in each so you catch a failure before food spoils.',
      type: 'stepper', field: 'fridge' },
    { key: 'pumps', title: 'How many bilge pumps?',
      help: 'We monitor how often each one runs — a pump cycling too often is the early warning of a leak.',
      type: 'stepper', field: 'pumps' },
    { key: 'bilges', title: 'How many bilges (separate compartments)?',
      help: 'A bilge is a compartment that can take on water. We add a high-water alarm to each.',
      type: 'stepper', field: 'bilges' },
    { key: 'hatches', title: 'How many doors or hatches do you want to monitor?',
      help: 'Wireless sensors alert you the instant a hatch, door, or locker opens — great for security and for catching one left open.',
      type: 'stepper', field: 'hatches' },
    { key: 'shore', title: 'Want an alert if shore (dock) power drops?',
      help: 'Know the instant the dock power trips and your batteries stop charging.',
      type: 'yesno', field: 'shore' },
    { key: 'nmea', title: 'Does your boat have a NMEA 2000® network?',
      help: 'Your boat’s marine data backbone. Roam reads whatever it carries — depth, wind, GPS, heading, tanks, engine data, etc. Not sure? Choose No — you can add it later.',
      type: 'yesno', field: 'nmea' }
  ];

  function Recommender(root) {
    this.root = root;
    this.app = root.querySelector('[data-rec-app]');
    this.builderUrl = root.getAttribute('data-builder-url') || '/pages/build-your-system';
    this.supportEmail = root.getAttribute('data-support-email') || 'support@roamdevices.com';
    this.kitLabel = root.getAttribute('data-kit-label') || 'Recommended Roam System';
    try {
      this.products = JSON.parse(root.querySelector('[data-rec-products]').textContent || '{}');
    } catch (e) { this.products = {}; }
    this.state = { house: 1, starter: 0, gen: 0, chem: 'lead', bankChem: {}, fridge: 0, pumps: 1, bilges: 1, hatches: 0, shore: true, nmea: false };
    this.step = 0;
    this.render();
  }

  // ---- recommendation engine ----------------------------------------------
  Recommender.prototype.compute = function () {
    var s = this.state, items = [], notes = [], used = 0, pushedWireless = false;

    items.push({ handle: 'roam-hub', qty: 1, role: 'Roam Hub',
      why: 'The brain — monitors your main battery voltage, temperature, GPS and anchor watch out of the box.', tier: 'base' });

    // Priority 1 — bilge pumps (run-activity is wired-only)
    if (s.pumps > 0) {
      var pw = Math.min(s.pumps, WIRED_BUDGET - used);
      if (pw > 0) {
        items.push({ handle: 'secondary-battery-bilge-pump-sensor', qty: pw, role: 'Bilge pump monitor', wired: true,
          why: 'Run-activity monitoring on ' + (pw === s.pumps ? ('your ' + countWord(s.pumps, 'bilge pump')) : (pw + ' of your ' + s.pumps + ' bilge pumps')) + ' (wired).', tier: 'base' });
        used += pw;
      }
      var pumpOver = s.pumps - pw;
      if (pumpOver > 0) notes.push(pumpOver + ' bilge ' + (pumpOver === 1 ? 'pump has' : 'pumps have') + ' no cable input left for run-activity, so ' + (pumpOver === 1 ? 'it’s' : 'they’re') + ' covered by the high-water float alarm instead. Want full pump coverage? Email us and we’ll help.');
    }

    // Priority 2 — float switch per bilge
    if (s.bilges > 0) {
      var fw = Math.min(s.bilges, WIRED_BUDGET - used);
      if (fw > 0) {
        items.push({ handle: 'high-water-float-switch', qty: fw, role: 'High-water float switch', wired: true,
          why: 'High-water alarm for ' + (fw === s.bilges ? ('your ' + countWord(s.bilges, 'bilge')) : (fw + ' of your ' + s.bilges + ' bilges')) + ' (wired).', tier: 'base' });
        used += fw;
      }
      var fOver = s.bilges - fw;
      if (fOver > 0) {
        items.push({ handle: 'wireless-float-switch', qty: fOver, role: 'Wireless float switch',
          why: 'High-water alarm for your remaining ' + countWord(fOver, 'bilge') + ' — wireless, no cable input needed.', tier: 'advanced' });
        pushedWireless = true;
      }
    }

    // Battery banks — per-bank chemistry. The hub covers the FIRST bank's
    // voltage on IN1 for free; each LITHIUM bank gets a SmartShunt (voltage
    // alone can't give lithium state-of-charge); remaining LEAD-ACID banks get
    // voltage monitoring (wired first, overflow to wireless).
    var banks = bankList(s);
    var lithCount = 0, leadNeedV = 0;
    banks.forEach(function (b, i) {
      if (bankChemOf(s, b.key) === 'lith') lithCount++;
      else if (i > 0) leadNeedV++; // the first lead-acid bank is covered by the hub
    });
    if (lithCount > 0) {
      items.push({ handle: 'victron-smart-shunt-500a', qty: lithCount, role: 'Battery shunt (lithium)',
        why: 'True state-of-charge for your ' + countWord(lithCount, 'lithium bank') + ' — voltage alone isn’t reliable for lithium.', tier: 'advanced' });
    }
    if (leadNeedV > 0) {
      var bw = Math.min(leadNeedV, WIRED_BUDGET - used);
      if (bw > 0) {
        items.push({ handle: 'secondary-battery-bilge-pump-sensor', qty: bw, role: 'Battery voltage monitor', wired: true,
          why: 'Voltage on ' + countWord(bw, 'lead-acid bank') + ' (wired).', tier: 'base' });
        used += bw;
      }
      var bOver = leadNeedV - bw;
      if (bOver > 0) {
        items.push({ handle: 'victron-smart-battery-sense', qty: bOver, role: 'Battery sense (wireless)',
          why: 'Voltage & temperature on ' + countWord(bOver, 'more lead-acid bank') + ' — wireless.', tier: 'advanced' });
        pushedWireless = true;
      }
    }

    // Doors / hatches
    if (s.hatches > 0) {
      items.push({ handle: 'roam-devices-hatch-sensor', qty: s.hatches, role: 'Hatch/door sensor',
        why: 'Alerts you the instant ' + (s.hatches === 1 ? 'this hatch or door opens' : 'any of your ' + s.hatches + ' hatches/doors opens') + ' — wireless, plus tamper detection.', tier: 'advanced' });
    }
    // Fridge / freezer
    if (s.fridge > 0) {
      items.push({ handle: 'ruuvitag', qty: s.fridge, role: 'Temperature sensor',
        why: 'Temperature in ' + (s.fridge === 1 ? 'your fridge or freezer' : 'your ' + s.fridge + ' fridges/freezers') + ' — wireless.', tier: 'advanced' });
    }
    // Shore power
    if (s.shore) {
      items.push({ handle: 'wireless-shore-power-sensor', qty: 1, role: 'Shore power sensor',
        why: 'Alerts you the moment dock power drops — wireless, so it keeps a cable input free.', tier: 'advanced' });
    }
    // NMEA 2000
    if (s.nmea) {
      items.push({ handle: 'nmea-2000-cable', qty: 1, role: 'NMEA 2000 cable',
        why: 'Reads whatever your NMEA 2000® network carries — depth, wind, GPS, heading, tanks, engine data, etc.', tier: 'premium' });
    }

    if (pushedWireless) notes.unshift('The Roam Hub has 3 cable inputs. We put your highest-priority sensors (bilge pumps, then float switches, then batteries) on cables and moved the rest to wireless — no extra wiring.');

    // drop any item whose product isn't in the store map; warn
    var self = this, missing = [];
    items = items.filter(function (it) {
      if (self.products[it.handle]) return true;
      missing.push(it.handle); return false;
    });
    if (missing.length) notes.push('A couple of items aren’t available to add online yet — email us and we’ll sort it out.');

    var tier = 'base';
    items.forEach(function (it) { if (TIER_RANK[it.tier] > TIER_RANK[tier]) tier = it.tier; });

    var total = 0;
    items.forEach(function (it) { total += (self.products[it.handle].price || 0) * it.qty; });

    return { items: items, notes: notes, tier: tier, total: total };
  };

  function countWord(n, word) { return n + ' ' + word + (n === 1 ? '' : 's'); }

  // Enumerate individual banks from the house/starter/gen counts.
  function bankList(s) {
    var out = [];
    [['house', 'House bank'], ['starter', 'Starter bank'], ['gen', 'Generator bank']].forEach(function (pair) {
      var n = +s[pair[0]] || 0;
      for (var i = 0; i < n; i++) out.push({ key: pair[0] + '-' + i, label: n > 1 ? pair[1] + ' ' + (i + 1) : pair[1] });
    });
    return out;
  }
  // Per-bank chemistry: uniform for lead/lithium, per-bank choice for mixed.
  function bankChemOf(s, key) {
    if (s.chem === 'lithium') return 'lith';
    if (s.chem === 'lead') return 'lead';
    return (s.bankChem && s.bankChem[key] === 'lith') ? 'lith' : 'lead';
  }

  // Minimum plan the selected hardware needs. Wireless -> Advanced, NMEA2000 ->
  // Premium are *required*; Base is *recommended* (Free WiFi-only also exists).
  function tierInfo(items) {
    var tier = 'base';
    items.forEach(function (it) { if (it.qty > 0 && TIER_RANK[it.tier] > TIER_RANK[tier]) tier = it.tier; });
    var verb = tier === 'base' ? 'recommended' : 'required';
    var reason;
    if (tier === 'premium') reason = 'NMEA 2000 data needs the Premium plan.';
    else if (tier === 'advanced') reason = 'The wireless sensors in this system report over Roam’s network, which needs the Advanced plan.';
    else reason = 'Covers cellular monitoring anywhere — or run free on your own WiFi when in range.';
    return { tier: tier, verb: verb, reason: reason };
  }

  // ---- rendering -----------------------------------------------------------
  // Active steps: the per-bank chemistry step only appears for a "mixed" fleet.
  Recommender.prototype.steps = function () {
    var s = this.state;
    return STEPS.filter(function (st) { return st.key !== 'bankchem' || s.chem === 'mixed'; });
  };

  Recommender.prototype.render = function () {
    var steps = this.steps();
    if (this.step > steps.length - 1) this.step = steps.length - 1;
    var step = steps[this.step];
    var pct = Math.round((this.step / steps.length) * 100);
    var h = '';
    h += '<div class="roam-rec__progress"><span style="width:' + pct + '%"></span></div>';
    h += '<p class="roam-rec__count">Question ' + (this.step + 1) + ' of ' + STEPS.length + '</p>';
    h += '<div class="roam-rec__step">';
    h += '<h3 class="roam-rec__q">' + esc(step.title) + '</h3>';
    if (step.help) h += '<p class="roam-rec__help">' + esc(step.help) + '</p>';
    h += '<div class="roam-rec__control">' + this.controlHtml(step) + '</div>';
    h += '</div>';
    h += '<div class="roam-rec__nav">';
    if (this.step > 0) h += '<button type="button" class="button button--secondary" data-rec-back>Back</button>';
    var last = this.step === steps.length - 1;
    h += '<button type="button" class="button button--primary" data-rec-next>' + (last ? 'See my system' : 'Next') + '</button>';
    h += '</div>';
    this.app.innerHTML = h;
    this.bindStep(step);
  };

  Recommender.prototype.controlHtml = function (step) {
    var s = this.state;
    if (step.type === 'steppers') {
      return step.fields.map(function (f) {
        return '<div class="roam-rec__stepper-row"><span>' + esc(f.label) + '</span>' + stepperHtml(f.id, s[f.id]) + '</div>';
      }).join('');
    }
    if (step.type === 'stepper') {
      return '<div class="roam-rec__stepper-row roam-rec__stepper-row--solo">' + stepperHtml(step.field, s[step.field]) + '</div>';
    }
    if (step.type === 'choice') {
      return '<div class="roam-rec__choices">' + step.options.map(function (o) {
        return '<button type="button" class="roam-rec__choice' + (s[step.field] === o.v ? ' is-on' : '') + '" data-rec-choice="' + o.v + '">' + esc(o.l) + '</button>';
      }).join('') + '</div>';
    }
    if (step.type === 'yesno') {
      return '<div class="roam-rec__choices">' +
        '<button type="button" class="roam-rec__choice' + (s[step.field] ? ' is-on' : '') + '" data-rec-yn="1">Yes</button>' +
        '<button type="button" class="roam-rec__choice' + (!s[step.field] ? ' is-on' : '') + '" data-rec-yn="0">No</button>' +
        '</div>';
    }
    if (step.type === 'bankchem') {
      var banks = bankList(s);
      if (!banks.length) return '<p class="roam-rec__help">Add at least one battery bank first (go back a step).</p>';
      return banks.map(function (b) {
        var c = bankChemOf(s, b.key);
        return '<div class="roam-rec__stepper-row"><span>' + esc(b.label) + '</span>' +
          '<span class="roam-rec__bankpick">' +
          '<button type="button" class="roam-rec__choice' + (c === 'lead' ? ' is-on' : '') + '" data-rec-bank="' + b.key + '" data-rec-bankval="lead">Lead-acid</button>' +
          '<button type="button" class="roam-rec__choice' + (c === 'lith' ? ' is-on' : '') + '" data-rec-bank="' + b.key + '" data-rec-bankval="lith">Lithium</button>' +
          '</span></div>';
      }).join('');
    }
    return '';
  };

  function stepperHtml(id, val) {
    return '<span class="roam-rec__stepper" data-rec-stepper="' + id + '">' +
      '<button type="button" data-rec-step="-1" aria-label="Decrease">−</button>' +
      '<input type="text" inputmode="numeric" value="' + (val || 0) + '" data-rec-val readonly>' +
      '<button type="button" data-rec-step="1" aria-label="Increase">+</button></span>';
  }

  Recommender.prototype.bindStep = function (step) {
    var self = this, s = this.state;
    var back = this.app.querySelector('[data-rec-back]');
    if (back) back.addEventListener('click', function () { self.step--; self.render(); });
    this.app.querySelector('[data-rec-next]').addEventListener('click', function () {
      if (self.step === self.steps().length - 1) self.showResult();
      else { self.step++; self.render(); }
    });
    this.app.querySelectorAll('[data-rec-stepper]').forEach(function (wrap) {
      var id = wrap.getAttribute('data-rec-stepper');
      var input = wrap.querySelector('[data-rec-val]');
      wrap.querySelectorAll('[data-rec-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(btn.getAttribute('data-rec-step'), 10));
          s[id] = next; input.value = next;
        });
      });
    });
    this.app.querySelectorAll('[data-rec-choice]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        s[step.field] = btn.getAttribute('data-rec-choice');
        self.render();
      });
    });
    this.app.querySelectorAll('[data-rec-yn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        s[step.field] = btn.getAttribute('data-rec-yn') === '1';
        self.render();
      });
    });
    this.app.querySelectorAll('[data-rec-bank]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!s.bankChem) s.bankChem = {};
        s.bankChem[btn.getAttribute('data-rec-bank')] = btn.getAttribute('data-rec-bankval');
        self.render();
      });
    });
  };

  Recommender.prototype.showResult = function () {
    this.rec = this.compute();
    this.renderResult();
  };

  // Total wired sensors currently selected (must stay within the 3 cable inputs).
  Recommender.prototype.wiredUsed = function () {
    var n = 0;
    this.rec.items.forEach(function (it) { if (it.wired && it.qty > 0) n += it.qty; });
    return n;
  };

  Recommender.prototype.renderResult = function () {
    var self = this, p = this.products, rec = this.rec, s = this.state;
    var live = rec.items.filter(function (it) { return it.qty > 0; });
    var total = 0; live.forEach(function (it) { total += (p[it.handle].price || 0) * it.qty; });
    var ti = tierInfo(rec.items);
    var overWired = this.wiredUsed() > WIRED_BUDGET;

    // Carry the current selection (aggregated by handle) to the configurator.
    var agg = {};
    rec.items.forEach(function (it) { if (it.qty > 0) agg[it.handle] = (agg[it.handle] || 0) + it.qty; });
    var kit = Object.keys(agg).map(function (h) { return h + ':' + agg[h]; }).join(',');
    var builderHref = this.builderUrl + (this.builderUrl.indexOf('?') >= 0 ? '&' : '?') + 'kit=' + encodeURIComponent(kit);

    var h = '<div class="roam-rec__result">';
    h += '<p class="roam-rec__count">Your recommended system</p>';
    h += '<h3 class="roam-rec__q">Built for ' + esc(boatSummary(s)) + '</h3>';
    h += '<p class="roam-rec__help">Adjust anything below — your total and plan update as you go.</p>';
    h += '<ul class="roam-rec__lines">';
    rec.items.forEach(function (it, idx) {
      var unit = p[it.handle].price || 0;
      var locked = it.handle === 'roam-hub';
      h += '<li class="roam-rec__line' + (it.qty <= 0 ? ' is-zero' : '') + '">';
      h += '<div class="roam-rec__line-top"><span class="roam-rec__line-name">' + esc(it.role) + '</span>';
      if (locked) {
        h += '<span class="roam-rec__line-qty roam-rec__line-qty--fixed">×1</span>';
      } else {
        h += '<span class="roam-rec__line-qty">' +
          '<button type="button" data-rec-line="' + idx + '" data-rec-d="-1" aria-label="Decrease">−</button>' +
          '<span>' + it.qty + '</span>' +
          '<button type="button" data-rec-line="' + idx + '" data-rec-d="1" aria-label="Increase">+</button></span>';
      }
      h += '<span class="roam-rec__line-price">' + (it.qty > 0 ? formatMoney(unit * it.qty) : '—') + '</span>';
      h += '</div><p class="roam-rec__line-why">' + esc(it.why) + '</p></li>';
    });
    h += '</ul>';
    if (overWired) h += '<p class="roam-rec__addnote is-error">The Roam Hub has ' + WIRED_BUDGET + ' cable inputs — you have ' + this.wiredUsed() + ' wired sensors selected. Reduce to ' + WIRED_BUDGET + ', or swap one for a wireless sensor.</p>';
    h += '<div class="roam-rec__total"><span>Hardware total</span><strong>' + formatMoney(total) + '</strong></div>';
    h += '<div class="roam-rec__plan"><strong>' + TIERS[ti.tier].name + ' plan ' + ti.verb + '</strong> — from ' + TIERS[ti.tier].price + '. <span>' + ti.reason + '</span></div>';
    if (rec.notes.length) {
      h += '<ul class="roam-rec__notes">';
      rec.notes.forEach(function (n) { h += '<li>' + esc(n) + '</li>'; });
      h += '</ul>';
    }
    h += '<button type="button" class="button button--primary roam-rec__add" data-rec-add' + (overWired || !live.length ? ' disabled' : '') + '>Add this system to cart</button>';
    h += '<p class="roam-rec__addnote" data-rec-note hidden></p>';
    h += '<div class="roam-rec__result-links">' +
      '<a href="' + esc(builderHref) + '">Fine-tune it in the builder</a>' +
      '<button type="button" data-rec-restart>Start over</button>' +
      '<a href="' + esc(this.contactHref(rec)) + '">Rather talk to a human?</a>' +
      '</div>';
    h += '</div>';
    this.app.innerHTML = h;

    this.app.querySelectorAll('[data-rec-line]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var it = rec.items[parseInt(btn.getAttribute('data-rec-line'), 10)];
        it.qty = Math.max(0, it.qty + parseInt(btn.getAttribute('data-rec-d'), 10));
        self.renderResult();
      });
    });
    this.app.querySelector('[data-rec-restart]').addEventListener('click', function () { self.step = 0; self.render(); });
    var add = this.app.querySelector('[data-rec-add]');
    if (add && !add.disabled) add.addEventListener('click', function () { self.addToCart(); });
  };

  function boatSummary(s) {
    var banks = (+s.house || 0) + (+s.starter || 0) + (+s.gen || 0);
    var chemLabel;
    if (s.chem === 'mixed') {
      var nl = bankList(s).filter(function (b) { return bankChemOf(s, b.key) === 'lith'; }).length;
      chemLabel = 'mixed — ' + nl + ' lithium';
    } else { chemLabel = { lead: 'lead-acid', lithium: 'lithium' }[s.chem]; }
    var parts = [countWord(banks, 'battery bank') + ' (' + chemLabel + ')'];
    if (s.pumps > 0) parts.push(countWord(s.pumps, 'bilge pump'));
    if (s.bilges > 0) parts.push(countWord(s.bilges, 'bilge'));
    if (s.hatches > 0) parts.push(countWord(s.hatches, 'hatch/door'));
    if (s.fridge > 0) parts.push(countWord(s.fridge, 'fridge/freezer'));
    if (s.shore) parts.push('shore power');
    if (s.nmea) parts.push('NMEA 2000');
    return parts.join(' · ');
  }

  Recommender.prototype.contactHref = function (rec) {
    var s = this.state;
    var body = 'Here is my boat and the system your tool suggested:\n\n' +
      'Battery banks: house ' + s.house + ', starter ' + s.starter + ', generator ' + s.gen + ' (' + s.chem + ')\n' +
      'Fridge/freezer: ' + s.fridge + '\nBilge pumps: ' + s.pumps + '\nBilges: ' + s.bilges + '\nHatches/doors: ' + s.hatches + '\n' +
      'Shore power: ' + (s.shore ? 'yes' : 'no') + '\nNMEA 2000: ' + (s.nmea ? 'yes' : 'no') + '\n\n' +
      'Suggested: ' + rec.items.map(function (it) { return it.role + (it.qty > 1 ? ' x' + it.qty : ''); }).join(', ') + '\n' +
      'Plan: ' + TIERS[rec.tier].name;
    return '/pages/contact?subject=' + encodeURIComponent('Help me choose a Roam system') + '&body=' + encodeURIComponent(body);
  };

  Recommender.prototype.addToCart = function () {
    var self = this, p = this.products, kid = kitId(), btn = this.app.querySelector('[data-rec-add]');
    var note = this.app.querySelector('[data-rec-note]');
    if (this.wiredUsed() > WIRED_BUDGET) {
      if (note) { note.hidden = false; note.textContent = 'Too many wired sensors — reduce to ' + WIRED_BUDGET + ' to continue.'; note.classList.add('is-error'); }
      return;
    }
    var live = this.rec.items.filter(function (it) { return it.qty > 0; });
    if (!live.length) return;
    var payload = { items: live.map(function (it) {
      return { id: Number(p[it.handle].id), quantity: it.qty,
        properties: { _kit_id: kid, _kit: self.kitLabel, 'System role': it.role } };
    }) };
    btn.disabled = true;
    if (note) { note.hidden = false; note.textContent = 'Adding your system to the cart…'; note.classList.remove('is-error'); }
    fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Add to cart failed'); });
      return r.json();
    }).then(function () { window.location.href = '/cart'; })
      .catch(function (err) {
        if (note) { note.hidden = false; note.textContent = err.message || 'Something went wrong. Please try again.'; note.classList.add('is-error'); }
        btn.disabled = false;
      });
  };

  function init() {
    var roots = document.querySelectorAll('[data-recommender]');
    for (var i = 0; i < roots.length; i++) new Recommender(roots[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
